import React, { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";

import { Input, FormGroup, Checkbox, MarkerProps, Map } from "@cofano/cck";
import { Box, IconButton } from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";
import SimpleLocationMarker from "view/maps/components/markers/SimpleLocationMarker";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import { request } from "components/utils/Request2";

import { hasPluginPermission, Language } from "imports/Utils";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import styles from "view/maps/utils/Styles.json";
import { ShipFromQuery } from "view/ships/ShipDetails";
import { shipAvailabilitySchema, ShipAvailabilitySchemaInput, ShipAvailabilitySchemaOutput } from "view/ships/utils/ship.schema";

import { ChildPropsFromArray } from "types/commons";
import { InsertOrUpdateAvailabilityExceptionDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";
import { AvailabilityExceptionType } from "__generated__/enums";

type Availability = ChildPropsFromArray<ShipFromQuery, "availabilityExceptions">;

interface ShipAvailabilityCreateEditDialogProps {
	open: boolean;
	availability: Availability | null;
	defaultAvailability: boolean;
	shipId: number;
	onClose: () => void;
	refetch: () => void;
}

const ShipAvailabilityCreateEditDialog = ({
	open,
	availability,
	defaultAvailability,
	shipId,
	onClose,
	refetch,
}: ShipAvailabilityCreateEditDialogProps) => {
	const session = useStoreState(store => store.session);

	const [ map, setMap ] = useState<{
		open: boolean,
		isStartLocation: boolean,
		markers: MarkerProps[],
	}>({
		open: false,
		isStartLocation: true,
		markers: [],
	});

	const [ saveAvailability, { loading } ] = useMutation(InsertOrUpdateAvailabilityExceptionDocument);

	const defaultValues = {
		id: null,
		allDay: false,
		startDate: null,
		endDate: null,
		startMasterdataLocation: null,
		startLocation: "",
		startPosition: {
			latitude: null,
			longitude: null,
		},
		endMasterdataLocation: null,
		endLocation: "",
		endPosition: {
			latitude: null,
			longitude: null,
		},
		remark: "",
	};

	const { control, handleSubmit, reset, setValue } = useForm<ShipAvailabilitySchemaInput, unknown, ShipAvailabilitySchemaOutput>({
		resolver: zodResolver(shipAvailabilitySchema(session)),
		defaultValues: defaultValues,
	});

	const [ startPosition, endPosition, allDay ] = useWatch({ control, name: [ "startPosition", "endPosition", "allDay" ] });

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const selectLocation = (lat: number, lng: number) => {

		const isStart = map.isStartLocation;

		setMap({
			open: false,
			isStartLocation: true,
			markers: [],
		});

		request<{
			id: number,
			name: string,
			location?: {
				city: string
			}
		}>({
			url: "geocode/reverse",
			method: "POST",
			body: {
				latitude: lat,
				longitude: lng,
			},
		}).then(response => {
			if (isStart) {
				setValue("startLocation", response.location?.city ?? "");
				setValue("startPosition", { latitude: lat, longitude: lng });
			} else {
				setValue("endLocation", response.location?.city ?? "");
				setValue("endPosition", { latitude: lat, longitude: lng });
			}
		});
	};

	const save = (data: ShipAvailabilitySchemaOutput) => {
		saveAvailability({
			variables: {
				input: [ {
					id: data.id ?? undefined,
					ship: {
						id: shipId,
					},
					type: defaultAvailability ? AvailabilityExceptionType.Availability : AvailabilityExceptionType.Exception,
					startDate: data.allDay ? moment(data.startDate).startOf("day").unix() * 1000 : data.startDate,
					endDate: data.allDay ? moment(data.endDate).endOf("day").unix() * 1000 : data.endDate,
					startMasterdataLocation: data.startMasterdataLocation ?? undefined,
					startLocation: data.startLocation || undefined,
					startPosition: data.startPosition.latitude ? data.startPosition : undefined,
					endMasterdataLocation: data.endMasterdataLocation ?? undefined,
					endLocation: data.endLocation || undefined,
					endPosition: data.endPosition.latitude ? data.endPosition : undefined,
					remark: data.remark,
				} ],
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open && availability) {
			reset({
				id: availability.id ?? null,
				allDay: false,
				startDate: availability.startDate ?? null,
				endDate: availability.endDate ?? null,
				startMasterdataLocation: availability.startMasterdataLocation ?? null,
				startLocation: availability.startLocation ?? "",
				startPosition: {
					latitude: null,
					longitude: null,
				},
				endMasterdataLocation: availability.endMasterdataLocation ?? null,
				endLocation: availability.endLocation ?? "",
				endPosition: {
					latitude: null,
					longitude: null,
				},
				remark: availability.remark ?? "",
			});
		} else {
			reset(defaultValues);
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "SHIP_AVAILABILITY" })}
			form='shipAvailabilityForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="shipAvailabilityForm">
				<FormGroup label={Language("ALL_DAY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="allDay"
						control={control}
						render={({ field }) => <Checkbox checked={field.value} {...field} value={undefined} />} />
				</FormGroup>

				<FormGroup
					label={Language("SHIP_AVAILABILITY_STARTDATE")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="startDate"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} showTimeInput={!allDay} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={Language("SHIP_AVAILABILITY_ENDDATE")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="endDate"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} showTimeInput={!allDay} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={Language("SAILINGREPORT_STARTLOCATION")} labelPlacement="left-center" overflow="hidden">
					{hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations") ? (
						<Controller
							name="startMasterdataLocation"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<LocationSelect {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					) : (
						<Controller
							name="startLocation"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									disabled
									suffixIcon={(
										<IconButton
											size="small"
											onClick={() => {
												setMap({
													open: true,
													isStartLocation: true,
													markers:  startPosition?.latitude && startPosition.longitude ? [ {
														lat: startPosition.latitude,
														lng: startPosition.longitude,
														component: () => <SimpleLocationMarker />,
													} ] : [],
												});
											}}>
											<RoomIcon fontSize="small" sx={{ color: (theme) => `${theme.palette.info.main} !important` }} />
										</IconButton>
									)} />
							)} />
					)}

					<Controller
						name="startPosition.latitude"
						control={control}
						render={({ field }) => <input {...field} value={field.value ?? ""} type="hidden" />} />

					<Controller
						name="startPosition.longitude"
						control={control}
						render={({ field }) => <input {...field} value={field.value ?? ""} type="hidden" />} />
				</FormGroup>

				<FormGroup label={Language("SAILINGREPORT_ENDLOCATION")} labelPlacement="left-center" overflow="hidden">
					{hasPluginPermission(session, "availabilityExceptionSelectMasterdataLocations") ? (
						<Controller
							name="endMasterdataLocation"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<LocationSelect {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					) : (
						<Controller
							name="endLocation"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									disabled
									suffixIcon={(
										<IconButton
											size="small"
											onClick={() => {
												setMap({
													open: true,
													isStartLocation: false,
													markers:  endPosition?.latitude && endPosition.longitude ? [ {
														lat: endPosition.latitude,
														lng: endPosition.longitude,
														component: () => <SimpleLocationMarker />,
													} ] : [],
												});
											}}>
											<RoomIcon fontSize="small" sx={{ color: (theme) => `${theme.palette.info.main} !important` }} />
										</IconButton>
									)} />
							)} />
					)}

					<Controller
						name="endPosition.latitude"
						control={control}
						render={({ field }) => <input {...field} value={field.value ?? ""} type="hidden" />} />

					<Controller
						name="endPosition.longitude"
						control={control}
						render={({ field }) => <input {...field} value={field.value ?? ""} type="hidden" />} />
				</FormGroup>

				<FormGroup label={Language("SHIP_AVAILABILITY_REMARK")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="remark"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>

			{map.open && (
				<Box
					sx={{
						".map": {
							bottom: 0,
							height: "auto",
							left: 0,
							position: "absolute",
							top: 0,
							width: "100%",
						},
					}}>
					<Map
						apiKey={"AIzaSyAFEhlhSJ7c85rDGz1JX99veD0sshXx_xs"}
						markers={map.markers}
						zoom={12}
						styles={styles}
						onClick={(lat, lng) => selectLocation(lat, lng)} />
				</Box>
			)}
		</FormDialog>
	);
};

export default ShipAvailabilityCreateEditDialog;
